import { GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { useCallback } from 'react';
import { FindActivitiesDocument, FindActivitiesQuery, FindActivitiesQueryVariables } from 'gql';
import { fetchData } from 'gql/graphql-fetcher';
import { ProgressCallback, useExcelExport } from '../../../utils/dataGridExport';

const exportPageSize = 500;

type ActivityRow = NonNullable<NonNullable<FindActivitiesQuery['activities']>['items']>[number];

interface Params {
  columns?: GridColDef<ActivityRow>[];
  columnVisibilityModel?: GridColumnVisibilityModel;
  queryOptions: Omit<FindActivitiesQueryVariables, 'pageSize' | 'skip'>;
  totalCount: number;
  isFetchingActivities: boolean;
  isFetchingUser: boolean;
}

export const useExportActivitiesAsExcel = ({
  columns,
  columnVisibilityModel,
  queryOptions,
  totalCount,
  isFetchingActivities,
  isFetchingUser
}: Params) => {
  const fetchAllActivities = useCallback(async (onProgress: ProgressCallback) => {
    if (totalCount === 0) return [];

    const allItems: ActivityRow[] = [];
    let skipCount = 0;

    while (skipCount < totalCount) {
      const response = await fetchData<FindActivitiesQuery, FindActivitiesQueryVariables>(FindActivitiesDocument, {
        ...queryOptions,
        pageSize: Math.min(exportPageSize, totalCount - skipCount),
        skip: skipCount
      })();

      const items = response?.activities?.items ?? [];
      allItems.push(...items);

      if (items.length === 0) {
        break;
      }

      skipCount += items.length;
      onProgress({ phase: 'fetching', percentage: Math.round((skipCount / totalCount) * 100) });
    }

    return allItems;
  }, [queryOptions, totalCount]);

  const { handleExport, exporting, exportDisabled: baseExportDisabled, progress } = useExcelExport<ActivityRow>({
    columns,
    columnVisibilityModel,
    fetchAll: fetchAllActivities,
    fileNamePrefix: 'activities'
  });

  const exportDisabled = baseExportDisabled || isFetchingActivities || isFetchingUser || totalCount === 0;

  return {
    handleExport,
    exporting,
    exportDisabled,
    progress
  };
};

